import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'

export default function Tooltip({ button, content }) {
  const [tooltipPosition, setTooltipPosition] = useState('center')
  const [tooltipVertical, setTooltipVertical] = useState('bottom')
  const [tooltipWidth, setTooltipWidth] = useState(280)
  const triggerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [isVisible, setIsVisible] = useState(false)
  const hideTimeoutRef = useRef(null)

  useEffect(() => {
    const checkPosition = () => {
      if (triggerRef.current && tooltipRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        
        // Tooltip width is max 320px or 50% of screen width, whichever is smaller
        const calculatedWidth = Math.min(320, viewportWidth * 0.5, viewportWidth - 32)
        
        // Determine position based on trigger location
        const triggerCenter = triggerRect.left + (triggerRect.width / 2)
        const screenQuarter = viewportWidth * 0.25
        const screenHalf = viewportWidth * 0.5
        
        let position = 'center'
        
        if (triggerCenter < screenQuarter) {
          // Left 25% of screen - open to the right
          position = 'right'
        } else if (triggerCenter > screenHalf + screenQuarter) {
          // Right 25% of screen - open to the left
          position = 'left'
        } else {
          // Middle 50% of screen - open in center
          position = 'center'
        }
        
        // Determine vertical position based on available space
        const tooltipHeight = 200 // Approximate tooltip height
        const viewportHeight = window.innerHeight
        
        // Check if there's enough space below
        const spaceBelow = viewportHeight - triggerRect.bottom
        const spaceAbove = triggerRect.top
        
        const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight
        const vertical = showAbove ? 'top' : 'bottom'
        
        setTooltipPosition(position)
        setTooltipVertical(vertical)
        setTooltipWidth(calculatedWidth)
      }
    }

    // Check position on mount and window resize
    checkPosition()
    window.addEventListener('resize', checkPosition)
    
    return () => window.removeEventListener('resize', checkPosition)
  }, [])

  const getTooltipClasses = () => {
    const baseClasses = "absolute px-3 py-2 bg-gray-800 text-white text-xs rounded-md transition-opacity duration-200 z-50"
    const visibilityClasses = isVisible ? " opacity-100 pointer-events-auto" : " opacity-0 pointer-events-none"
    
    // Vertical positioning
    const verticalClasses = tooltipVertical === 'top' 
      ? "bottom-full mb-2" 
      : "top-full mt-2"
    
    // Horizontal positioning
    let horizontalClasses = ""
    switch (tooltipPosition) {
      case 'left':
        // Position tooltip to the left, but offset so arrow points to button center
        horizontalClasses = "right-0 transform translate-x-[14px]"
        break
      case 'right':
        // Position tooltip to the right, but offset so arrow points to button center
        horizontalClasses = "left-0 transform -translate-x-[10px]"
        break
      default:
        horizontalClasses = "left-1/2 transform -translate-x-1/2"
    }
    
    return `${baseClasses}${visibilityClasses} ${verticalClasses} ${horizontalClasses}`
  }

  const getArrowClasses = () => {
    const baseClasses = "absolute w-0 h-0 border-l-4 border-r-4 border-transparent"
    
    // Vertical arrow positioning
    const verticalArrowClasses = tooltipVertical === 'top'
      ? "top-full border-t-4 border-t-gray-800"
      : "bottom-full border-b-4 border-b-gray-800"
    
    // Horizontal arrow positioning
    let horizontalArrowClasses = ""
    switch (tooltipPosition) {
      case 'left':
        horizontalArrowClasses = "right-4 transform translate-x-0"
        break
      case 'right':
        horizontalArrowClasses = "left-4 transform translate-x-0"
        break
      default:
        horizontalArrowClasses = "left-1/2 transform -translate-x-1/2"
    }
    
    return `${baseClasses} ${verticalArrowClasses} ${horizontalArrowClasses}`
  }

  const showTooltip = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    setIsVisible(true)
  }

  const scheduleHideTooltip = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 250)
  }

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={scheduleHideTooltip}
      >
        {button}
      </div>
      <div
        ref={tooltipRef}
        className={getTooltipClasses()}
        style={{ width: `${tooltipWidth}px` }}
        onMouseEnter={showTooltip}
        onMouseLeave={scheduleHideTooltip}
      >
        <div className="whitespace-pre-wrap leading-relaxed">
          {content}
        </div>
        <div className={getArrowClasses()}></div>
      </div>
    </div>
  )
}

Tooltip.propTypes = {
  button: PropTypes.element.isRequired,
  content: PropTypes.element.isRequired,
} 